import { useEffect, useState } from 'react';
import {
  Avatar as ChakraAvatar,
  forwardRef,
  type AvatarProps as ChakraAvatarProps,
} from '@chakra-ui/react';
import { onPicturesChanged, openPicture } from '../lib/e2ee/pictures';

// Faces from sealed pictures (M72, D294). Every picture arrives as an
// address (`/api/avatars/<hash>`, D277) whose bytes are sealed: it opens on
// this device, into an object URL kept for the visit, and until then the
// person's initials show. A face is opened as someone's (`ownerId`) on some
// family's screen (`familyId`), never across families (D292); without an
// owner it isn't opened at all (join pages, invitations, the admin panel,
// D296). Anything that isn't an address (a picker's preview) passes through.

const ADDRESS_PREFIX = '/api/avatars/';

export interface PictureOwner {
  /** The person, family or group chat whose face this is. */
  ownerId?: string | null;
  /** The family of the screen it's on; for a family, the family itself. */
  familyId?: string | null;
}

/** The picture to show for `src`: opened, passed through, or undefined. */
export function usePicture(
  src: string | null | undefined,
  { ownerId, familyId }: PictureOwner
): string | undefined {
  const sealed = Boolean(src?.startsWith(ADDRESS_PREFIX));
  const id =
    src && sealed && ownerId ? `${src}|${familyId ?? ''}|${ownerId}` : null;
  const [opened, setOpened] = useState<{ id: string; url: string } | null>(
    null
  );

  useEffect(() => {
    if (!id || !src || !ownerId) return;
    let live = true;
    const open = () => {
      void openPicture(src, { familyId: familyId ?? null, ownerId }).then(
        (url) => {
          // null when it can't be opened (any more): initials, never a
          // stale face.
          if (live) setOpened(url ? { id, url } : null);
        }
      );
    };
    open();
    // Keys arriving, or a face changing, may open (or close) this one.
    const stop = onPicturesChanged(open);
    return () => {
      live = false;
      stop();
    };
  }, [id, src, ownerId, familyId]);

  if (!src) return undefined;
  if (!sealed) return src;
  return opened?.id === id ? opened.url : undefined;
}

export interface AvatarProps extends ChakraAvatarProps, PictureOwner {}

/** Chakra's Avatar, with `src` opened from its sealed picture (D294). */
export const Avatar = forwardRef<AvatarProps, 'span'>(function Avatar(
  { src, ownerId, familyId, ...rest },
  ref
) {
  const shown = usePicture(src, { ownerId, familyId });
  return <ChakraAvatar ref={ref} src={shown} {...rest} />;
});
